// 导入类型（在JavaScript中主要用于文档和IDE支持）
// import { Streamer, Region, Platform, Game } from '../types/index.js';
import * as XLSX from 'xlsx'

const IS_DEV = process.env.NODE_ENV === 'development';

const API_BASE_URL = '/api/v1/channel-selector';

/** 带认证的 fetch：通过 httpOnly cookie 认证，供所有 channel-selector 请求使用 */
function authFetch(url, options = {}) {
  const opts = { credentials: 'include', ...options };
  opts.headers = { ...(opts.headers || {}) };
  return fetch(url, opts);
}

// API functions
export const fetchStreamers = async (filters = {}) => {
  try {
    const { countryCodes, platformCodes, game = '0', category = 'game' } = filters;
    const qs = new URLSearchParams();
    qs.set('category', category);
    // 只有数组真的带元素才传
    if (countryCodes?.length) qs.set('countryCodes', JSON.stringify(countryCodes));
    if (platformCodes?.length)   qs.set('platforms', JSON.stringify(platformCodes));
    if (game && game !== '0') qs.set('game', game);
    const response = await authFetch(`${API_BASE_URL}/top100?${qs.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch streamers');
    const json = await response.json();
    const channels = json?.data.list || [];
    const selectedGameId = parseInt(game);
    const transformed = channels.map(ch => {
      const statsForGame = Array.isArray(ch.gameStats)
        ? ch.gameStats.find(s => s.gameId === selectedGameId)
        : null;
      return {
        id: ch.id,
        channelId: ch.channelId,
        name: ch.name,
        email: ch.email || '',
        country: ch.country || 'N/A',
        platform: ch.platform || 'YouTube',
        avatar: ch.thumbnails?.high?.url || ch.thumbnails?.default?.url || 'https://via.placeholder.com/64',
        follower_count: ch.subs ?? 0,
        home_page: ch.url || ch.customUrl || '',
        total_play_count: ch.views ?? 0,
        avg_play_count: statsForGame?.avgViews ?? 0,
        recent_play_count: statsForGame?.latestVideo?.views ?? 0,
        highest_play_count: statsForGame?.maxViewsVideo?.views ?? 0,
        recent_video_url: statsForGame?.latestVideo?.url || '',
        highest_video_url: statsForGame?.maxViewsVideo?.url || '',
      };
    });
    return {
      success: true,
      streamers: transformed,
      total: transformed.length
    };
  } catch (error) {
    console.error('Error fetching streamers:', error);
    throw error;
  }
};

export const fetchFilterOptions = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/options`);
    if (!response.ok) throw new Error('Failed to fetch filter options');
    const data = await response.json();
    return data.data
  } catch (error) {
    console.error('Error fetching filter options:', error);
    return {
      regions: [],
      platforms: [],
      games: []
    };
  }
};

export const fetchRegions = async (page = 1, pageSize = 10) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/regions?page=${page}&pageSize=${pageSize}`);
    if (!response.ok) throw new Error('Failed to fetch regions');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching regions:', error);
    if (IS_DEV) return { list: [], total: 0 };
    throw error;
  }
};

export const createRegion = async (regionData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/regions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(regionData)
    });
    if (!response.ok) throw new Error('Failed to create region');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error creating region:', error);
    throw error;
  }
};

export const updateRegion = async (regionId, regionData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/regions/${regionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(regionData)
    });
    if (!response.ok) throw new Error('Failed to update region');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error updating region:', error);
    throw error;
  }
};

export const deleteRegion = async (regionId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/regions/${regionId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete region');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error deleting region:', error);
    throw error;
  }
};

export const toggleRegionStatus = async (regionId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/regions/${regionId}/status`, {
      method: 'PATCH'
    });
    if (!response.ok) throw new Error('Failed to toggle region status');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error toggling region status:', error);
    throw error;
  }
};

export const fetchCountries = async (options = {}) => {
  try {
    const { excludeAssociated = false, excludeRegionId = null, page = 1, pageSize = 10, keyword = '', status } = options;
    let url = `${API_BASE_URL}/countries`;
    
    const params = new URLSearchParams();
    if (excludeAssociated) {
      params.append('excludeAssociated', 'true');
      if (excludeRegionId) {
        params.append('excludeRegionId', excludeRegionId);
      }
    } else {
      // 管理页面查询：支持分页和筛选
      params.append('page', page);
      params.append('pageSize', pageSize);
      if (keyword) params.append('keyword', keyword);
      if (status !== undefined && status !== '' && status !== 'all') params.append('status', status);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await authFetch(url);
    if (!response.ok) throw new Error('Failed to fetch countries');
    const result = await response.json();
    // 如果 excludeAssociated 为 true，返回数组；否则返回分页对象
    if (excludeAssociated) {
      return result.data || [];
    }
    return result.data || result;
  } catch (error) {
    console.error('Error fetching countries:', error);
    throw error;
  }
};

export const createCountry = async (countryData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/countries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(countryData)
    });
    if (!response.ok) throw new Error('Failed to create country');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error creating country:', error);
    throw error;
  }
};

export const updateCountry = async (countryId, countryData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/countries/${countryId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(countryData)
    });
    if (!response.ok) throw new Error('Failed to update country');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error updating country:', error);
    throw error;
  }
};

export const deleteCountry = async (countryId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/countries/${countryId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete country');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error deleting country:', error);
    throw error;
  }
};

export const toggleCountryStatus = async (countryId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/countries/${countryId}/status`, {
      method: 'PATCH'
    });
    if (!response.ok) throw new Error('Failed to toggle country status');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error toggling country status:', error);
    throw error;
  }
};

// 批量导入国家
export const importCountries = async (list) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/countries/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ list })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: result?.message || '批量导入失败'
      };
    }
    return result;
  } catch (error) {
    console.error('Error importing countries:', error);
    return { success: false, message: error?.message || '网络错误' };
  }
};

// 下载CSV模板（直接返回模板URL，供页面使用）
export const getCountryImportTemplateUrl = () => `${API_BASE_URL}/countries/import/template`;

// 批量导入地区
export const importRegions = async (list) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/regions/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ list })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: result?.message || '批量导入失败'
      };
    }
    return result;
  } catch (error) {
    console.error('Error importing regions:', error);
    return { success: false, message: error?.message || '网络错误' };
  }
};

// 下载地区CSV模板
export const getRegionImportTemplateUrl = () => `${API_BASE_URL}/regions/import/template`;

// 批量导入平台
export const importPlatforms = async (list) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/platforms/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ list })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: result?.message || '批量导入失败'
      };
    }
    return result;
  } catch (error) {
    console.error('Error importing platforms:', error);
    return { success: false, message: error?.message || '网络错误' };
  }
};

// 下载平台CSV模板
export const getPlatformImportTemplateUrl = () => `${API_BASE_URL}/platforms/import/template`;

export const fetchPlatforms = async (page = 1, pageSize = 10) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/platforms?page=${page}&pageSize=${pageSize}`);
    if (!response.ok) throw new Error('Failed to fetch platforms');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching platforms:', error);
    throw error;
  }
};

export const createPlatform = async (platformData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/platforms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(platformData)
    });
    if (!response.ok) throw new Error('Failed to create platform');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error creating platform:', error);
    throw error;
  }
};

export const updatePlatform = async (platformId, platformData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/platforms/${platformId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(platformData)
    });
    if (!response.ok) throw new Error('Failed to update platform');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error updating platform:', error);
    throw error;
  }
};

export const deletePlatform = async (platformId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/platforms/${platformId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete platform');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error deleting platform:', error);
    throw error;
  }
};

export const togglePlatformStatus = async (platformId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/platforms/${platformId}/status`, {
      method: 'PATCH'
    });
    if (!response.ok) throw new Error('Failed to toggle platform status');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error toggling platform status:', error);
    throw error;
  }
};

export const addPlatformRegion = async (platformId, regionId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/platforms/${platformId}/regions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ regionId })
    });
    if (!response.ok) throw new Error('Failed to add platform region');
    const result = await response.json();
    return result.data
  } catch (error) {
    console.error('Error adding platform region:', error);
    throw error;
  }
};

export const removePlatformRegion = async (platformId, regionId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/platforms/${platformId}/regions/${regionId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to remove platform region');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error removing platform region:', error);
    throw error;
  }
};

export const fetchGames = async (page = 1, pageSize = 10) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/games?page=${page}&pageSize=${pageSize}`);
    if (!response.ok) throw new Error('Failed to fetch games');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching games:', error);
    throw error;
  }
};

export const createGame = async (gameData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/games`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gameData)
    });
    if (!response.ok) throw new Error('Failed to create game');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error creating game:', error);
    throw error;
  }
};

export const updateGame = async (gameId, gameData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/games/${gameId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gameData)
    });
    if (!response.ok) throw new Error('Failed to update game');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error updating game:', error);
    throw error;
  }
};

export const deleteGame = async (gameId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/games/${gameId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete game');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error deleting game:', error);
    throw error;
  }
};

export const toggleGameStatus = async (gameId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/games/${gameId}/status`, {
      method: 'PATCH'
    });
    if (!response.ok) throw new Error('Failed to toggle game status');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error toggling game status:', error);
    throw error;
  }
};

// 批量导入游戏
export const importGames = async (list) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/games/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ list })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: result?.message || '批量导入失败'
      };
    }
    return result;
  } catch (error) {
    console.error('Error importing games:', error);
    return { success: false, message: error?.message || '网络错误' };
  }
};

// 下载CSV模板（直接返回模板URL，供页面使用）
export const getGameImportTemplateUrl = () => `${API_BASE_URL}/games/import/template`;

export const fetchChannels = async (endpoint = 'channels', filters = {}) => {
  try {
    const { countryCodes, platformCodes, game = '0', name, page, pageSize, category = 'game' } = filters;
    const qs = new URLSearchParams();
    qs.set('category', category);
    // 分页参数
    if (page !== undefined) qs.set('page', page);
    if (pageSize !== undefined) qs.set('pageSize', pageSize);
    // 只有数组真的带元素才传
    if (countryCodes?.length) qs.set('countryCodes', JSON.stringify(countryCodes));
    if (platformCodes?.length) qs.set('platforms', JSON.stringify(platformCodes));
    if (game && game !== '0') qs.set('game', game);
    if (name && name !== '') qs.set('name', name);
    const response = await authFetch(`${API_BASE_URL}/${endpoint}?${qs.toString()}`);
    if (!response.ok) throw new Error(`Failed to fetch ${endpoint}`);
    const result = await response.json();
    
    // 处理后端返回的数据结构
    if (result.success && result.data) {
      // 对于 followed-channels 端点，数据结构是 {channels: [...], total: ...}
      if (result.data.channels) {
        return {
          channels: result.data.channels,
          total: result.data.total || 0
        };
      }
      // 对于 channels 端点，数据结构是 {list: [...], total: ...}
      else if (result.data.list) {
        return {
          channels: result.data.list,
          total: result.data.total || 0
        };
      }
      // 其他情况
      return {
        channels: result.data,
        total: result.data.length || 0
      };
    }
    
    return result.data || result;
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    if (IS_DEV) return { channels: [], total: 0 };
    throw error;
  }
};

export const updateChannel = async (channelId, channelData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/channels/${channelId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(channelData)
    });
    if (!response.ok) throw new Error('Failed to update channel');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error updating channel:', error);
    throw error;
  }
};

// 关注频道
export const followChannel = async (channelId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/follow-channel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelId })
    });
    if (!response.ok) throw new Error('Failed to follow channel');
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error following channel:', error);
    throw error;
  }
};

// 取消关注频道（单个）
export const deleteChannel = async (channelId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/follow-channel/${channelId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to unfollow channel');
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error unfollowing channel:', error);
    throw error;
  }
};

// 批量取消关注
export const batchUnfollowChannels = async (channelIds) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/batch-unfollow`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelIds })
    });
    if (!response.ok) throw new Error('Failed to batch unfollow channels');
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error batch unfollowing channels:', error);
    throw error;
  }
};


// 批量发送邮件
export const batchSendEmails = async (emails, emailData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/batch-send-emails`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emails, subject: emailData.subject, content: emailData.content })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result?.message || '批量发送失败');
    }
    return result;
  } catch (error) {
    console.error('Error sending batch emails:', error);
    throw error;
  }
};

export const getCrawlConfig = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/config`);
    if (!response.ok) throw new Error('Failed to get crawl config');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error getting crawl config:', error);
    throw error;
  }
};

export const updateCrawlConfig = async (config) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });
    if (!response.ok) throw new Error('Failed to update crawl config');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error updating crawl config:', error);
    throw error;
  }
};

// 启动游戏抓取任务
export const startCrawlTask = async (options = {}) => {
  try {
    return await createTask({
      type: 'auto',
      options: {
        minSubs: options.minSubs || 50000,
        channelConcurrency: options.channelConcurrency || 3,
        ...options
      }
    });
  } catch (error) {
    console.error('Error starting crawl task:', error);
    throw error;
  }
};

export const getCrawlTasks = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/tasks`);
    if (!response.ok) throw new Error('Failed to get crawl tasks');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error getting crawl tasks:', error);
    throw error;
  }
};

export const getCrawlPlatforms = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/platforms`);
    if (!response.ok) throw new Error('Failed to get crawl platforms');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error getting crawl platforms:', error);
    throw error;
  }
};

export const getCrawlRegions = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/regions`);
    if (!response.ok) throw new Error('Failed to get crawl regions');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error getting crawl regions:', error);
    throw error;
  }
};

// ==================== Excel 文件管理 API ====================

// 上传并解析 Excel 文件（不存储），返回 { fileName, sheets, sheetInfo }
export const uploadExcel = async (file) => {
  try {
    const form = new FormData();
    form.append('excel', file);
    const response = await authFetch(`${API_BASE_URL}/upload-excel`, {
      method: 'POST',
      body: form,
    });
    if (!response.ok) throw new Error('Failed to upload file');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error uploading excel:', error);
    throw error;
  }
};

// 列出历史上传的 Excel 文件
export const listUploadedExcels = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/uploaded-excels`);
    if (!response.ok) throw new Error('Failed to list uploaded excels');
    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error listing uploaded excels:', error);
    throw error;
  }
};

// 读取指定文件的 Sheet 列表
export const getExcelSheets = async (filePath) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/excel-sheets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filePath })
    });
    if (!response.ok) throw new Error('Failed to get excel sheets');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error getting excel sheets:', error);
    throw error;
  }
};


// 支持一次性导入多个 Sheet
export const importExcelDirect = async (file, sheetName) => {
  try {
    // sheetName 可以是数组或逗号分隔的字符串
    const sheetNameStr = Array.isArray(sheetName) ? sheetName.join(',') : String(sheetName);
    
    // 将 sheetName 作为 URL 查询参数传递（因为 FormData 在文件上传时无法获取其他字段）
    const url = `${API_BASE_URL}/import-excel-direct?sheetName=${encodeURIComponent(sheetNameStr)}`;
    
    const formData = new FormData();
    formData.append('excel', file);
    
    const response = await authFetch(url, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to import excel');
    }
    
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error importing excel:', error);
    throw error;
  }
};

// 删除已上传的Excel文件（保留用于向后兼容）
export const deleteExcel = async (filePath) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/delete-excel`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filePath })
    });
    if (!response.ok) throw new Error('Failed to delete excel');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error deleting excel:', error);
    throw error;
  }
};

// ==================== 调度器状态 API ====================

export const getSchedulerStatus = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/scheduler/status`);
    if (!response.ok) throw new Error('Failed to fetch scheduler status');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching scheduler status:', error);
    throw error;
  }
};

// ==================== 任务管理 API ====================

// 获取任务列表（支持分页和过滤）
export const getTasks = async (params = {}) => {
  try {
    const { page = 1, pageSize = 10, status = 'all' } = params;
    const qs = new URLSearchParams();
    qs.set('page', page);
    qs.set('pageSize', pageSize);
    if (status && status !== 'all') {
      qs.set('status', status);
    }
    const response = await authFetch(`${API_BASE_URL}/crawl/tasks?${qs.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch tasks');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching tasks:', error);
    throw error;
  }
};

// 获取单个任务详情
export const getTask = async (taskId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/tasks/${taskId}`);
    if (!response.ok) throw new Error('Failed to fetch task');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching task:', error);
    throw error;
  }
};

// 获取任务统计
export const getTaskStats = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/tasks/stats`);
    if (!response.ok) throw new Error('Failed to fetch task stats');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching task stats:', error);
    throw error;
  }
};

// 创建任务（游戏抓取或 Excel 导入）
export const createTask = async (taskData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(taskData)
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Failed to create task');
    }
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

// 提交 Excel 导入任务（后台执行）
export const importExcelTask = async ({ filePath, sheetName }) => {
  try {
    return await createTask({
      type: 'excel',
      options: { filePath, sheetName }
    });
  } catch (error) {
    console.error('Error creating import task:', error);
    throw error;
  }
};

// 重试失败的任务
export const retryTask = async (taskId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/tasks/${taskId}/retry`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to retry task');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error retrying task:', error);
    throw error;
  }
};

// 取消运行中的任务
export const cancelTask = async (taskId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/tasks/${taskId}/cancel`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to cancel task');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error canceling task:', error);
    throw error;
  }
};

// 删除任务
export const deleteTask = async (taskId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/crawl/tasks/${taskId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete task');
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};


export const stopScheduler = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/scheduler/stop`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Failed to stop scheduler');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error stopping scheduler:', error);
    throw error;
  }
};

export const executeSchedulerTask = async (taskType = 'fetch') => {
  try {
    const response = await authFetch(`${API_BASE_URL}/scheduler/execute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taskType })
    });
    if (!response.ok) throw new Error('Failed to execute scheduler task');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error executing scheduler task:', error);
    throw error;
  }
};

export const exportToExcel = async (data, filename = 'streamers.xlsx') => {
  try {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Streamers');

    // 如果数据中包含“标题”和“链接”字段，则把链接列转换为带标题的超链接
    if (Array.isArray(data) && data.length > 0) {
      const headers = Object.keys(data[0]);
      const titleColIndex = headers.indexOf('标题');
      const linkColIndex = headers.indexOf('链接');

      if (linkColIndex !== -1) {
        // 遍历每一行数据（从 Excel 第2行开始，因为第1行是表头）
        data.forEach((row, rowIdx) => {
          const url = row['链接'];
          if (!url) return;

          const title = titleColIndex !== -1 ? (row['标题'] || url) : url;
          const cellAddress = XLSX.utils.encode_cell({
            r: rowIdx + 1, // header 在第0行，这里是数据行
            c: linkColIndex,
          });

          if (!worksheet[cellAddress]) {
            worksheet[cellAddress] = { t: 's', v: url };
          } else {
            worksheet[cellAddress].t = 's';
            worksheet[cellAddress].v = url;
          }

          worksheet[cellAddress].l = {
            Target: url,
            Tooltip: title,
          };
        });
      }
    }

    XLSX.writeFile(workbook, filename);
    return { success: true };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};

export const fetchChannelVideos = async (channelId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/channel-videos?id=${channelId}`);
    if (!response.ok) throw new Error('Failed to fetch channel videos');
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching channel videos:', error);
    throw error;
  }
};

// 手动触发搜索短剧频道脚本
export const searchShortDramaChannels = async (maxResultsPerQuery = null, withinDays = null) => {
  try {
    const body = {};
    if (maxResultsPerQuery !== null) {
      body.maxResultsPerQuery = maxResultsPerQuery;
    }
    if (withinDays !== null) {
      body.withinDays = withinDays;
    }

    const response = await fetch('/api/v1/admin/tools/search-short-drama-channels', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || '搜索任务执行失败');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error searching short drama channels:', error);
    throw error;
  }
};

// 手动触发更新 YouTube Quest Videos 脚本
export const updateYoutubeQuestVideos = async (questId = null) => {
  try {
    const body = questId ? { questId } : {};

    const response = await fetch('/api/v1/admin/tools/update-youtube-quest-videos', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || '更新任务执行失败');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating YouTube quest videos:', error);
    throw error;
  }
};